/**
 * ScheduleService — Time-based availability logic.
 *
 * Hierarchical Waterfall: Profile → Category → Item
 * Even if an item is "Always On", if the profile or category is closed,
 * the item is considered closed.
 */

import type { AvailabilitySchedule, DayOfWeek, MenuCategory, MenuProfile } from '../types';

const DAY_MAP: Record<number, DayOfWeek> = {
  0: 'SUN', 1: 'MON', 2: 'TUE', 3: 'WED', 4: 'THU', 5: 'FRI', 6: 'SAT',
};

/** Check if a schedule allows visibility at the given date/time */
export function isAvailableNow(schedule: AvailabilitySchedule | undefined, now?: Date): boolean {
  if (!schedule || schedule.alwaysOn) return true;
  const d = now ?? new Date();
  const today = DAY_MAP[d.getDay()];
  if (!schedule.days.includes(today)) return false;
  if (!schedule.slots || schedule.slots.length === 0) return true;
  const hhmm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return schedule.slots.some(s => hhmm >= s.start && hhmm <= s.end);
}

/** Get the next available time slot label for a schedule */
export function nextSlotLabel(schedule: AvailabilitySchedule | undefined, now?: Date): string {
  if (!schedule || schedule.alwaysOn) return '';
  const d = now ?? new Date();
  const today = DAY_MAP[d.getDay()];
  const hhmm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  if (schedule.days.includes(today)) {
    const next = schedule.slots.find(s => s.start > hhmm);
    if (next) return `Reopens today at ${next.start}`;
  }
  const dayOrder: DayOfWeek[] = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const todayIdx = d.getDay();
  for (let i = 1; i <= 7; i++) {
    const nextDay = dayOrder[(todayIdx + i) % 7];
    if (schedule.days.includes(nextDay)) {
      const slot = schedule.slots[0];
      return `Reopens ${nextDay}${slot ? ` at ${slot.start}` : ''}`;
    }
  }
  return 'Currently closed';
}

/** Closure reason for waterfall cascade */
export type ClosureReason = 'emergency' | 'profile' | 'category' | 'item' | null;

export interface ItemAvailability { available: boolean; reason: ClosureReason; }

/** Waterfall availability: Profile → Category → Item */
export function getItemAvailability(
  profileSchedule: AvailabilitySchedule | undefined,
  catSchedule: AvailabilitySchedule | undefined,
  itemSchedule: AvailabilitySchedule | undefined,
  emergencyShutdown: boolean,
  now?: Date,
): ItemAvailability {
  if (emergencyShutdown) return { available: false, reason: 'emergency' };
  if (!isAvailableNow(profileSchedule, now)) return { available: false, reason: 'profile' };
  if (!isAvailableNow(catSchedule, now)) return { available: false, reason: 'category' };
  if (!isAvailableNow(itemSchedule, now)) return { available: false, reason: 'item' };
  return { available: true, reason: null };
}

/** Waterfall-aware filter for preview rendering */
export function filterByWaterfall(
  profile: MenuProfile,
  emergencyShutdown: boolean,
  now?: Date,
): {
  profileAvailable: boolean;
  profileNextSlot: string;
  categories: { id: string; available: boolean; reason: ClosureReason; items: { id: string; available: boolean; reason: ClosureReason }[] }[];
} {
  const d = now ?? new Date();
  const profileAvail = !emergencyShutdown && isAvailableNow(profile.availabilitySchedule, d);
  const profileNextSlot = emergencyShutdown ? 'Emergency shutdown active' : nextSlotLabel(profile.availabilitySchedule, d);

  return {
    profileAvailable: profileAvail,
    profileNextSlot,
    categories: profile.categories.map(cat => {
      const catAvail = profileAvail && isAvailableNow(cat.availabilitySchedule, d);
      const catReason: ClosureReason = !profileAvail ? (emergencyShutdown ? 'emergency' : 'profile') : !catAvail ? 'category' : null;
      return {
        id: cat.id,
        available: catAvail,
        reason: catReason,
        items: cat.items.map(item => {
          const avail = getItemAvailability(profile.availabilitySchedule, cat.availabilitySchedule, item.availabilitySchedule, emergencyShutdown, d);
          return { id: item.id, available: avail.available, reason: avail.reason };
        }),
      };
    }),
  };
}

/** Get a short human-readable label for a schedule */
export function scheduleLabel(schedule: AvailabilitySchedule | undefined): string {
  if (!schedule || schedule.alwaysOn) return 'Always available';
  const days = schedule.days.length === 7 ? 'Every day' : schedule.days.join(', ');
  if (!schedule.slots || schedule.slots.length === 0) return days;
  const slots = schedule.slots.map(s => `${s.start}–${s.end}`).join(', ');
  return `${days} • ${slots}`;
}
